import React from "react";
import {
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  useTheme,
} from "@mui/material";
import { gql, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";

// Define the mutation of update user password
const UPDATE_USER_PASSWORD = gql`
  mutation changePassword(
    $Id: ID!
    $oldPassword: String!
    $newPassword: String!
    $role: String!
  ) {
    changePassword(
      Id: $Id
      oldPassword: $oldPassword
      newPassword: $newPassword
      role: $role
    )
  }
`;

const UpdateUserPassword = ({ id, role }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [changePassword, { loading }] = useMutation(UPDATE_USER_PASSWORD);
  const [oldPassword, setOldPassword] = React.useState("");
  const [newPassword, setNewPassword] = React.useState("");
  const [confirmNewPassword, setConfirmNewPassword] = React.useState("");
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const showSnackbar = (severity, message) => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleUpdateProfile = async () => {
    if (!oldPassword || !newPassword || !confirmNewPassword) {
      showSnackbar("error", t("updatePassword.fillAllFields"));
      return;
    }
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
    if (!passwordRegex.test(newPassword)) {
      showSnackbar("error", t("updatePassword.passwordRequirements"));
      return;
    }
    if (newPassword !== confirmNewPassword) {
      showSnackbar("error", t("updatePassword.passwordMismatch"));
      return;
    }
    try {
      const response = await changePassword({
        variables: { Id: id, oldPassword, newPassword, role },
      });
      // console.log("response", response);
      if (response?.data?.changePassword) {
        showSnackbar("success", t("updatePassword.success"));
      }
    } catch (error) {
      console.error("Failed to update user password:", error);
      showSnackbar("error", error.message);
    }
  };

  return (
    <form
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.background.paper,
        padding: theme.spacing(3),
        borderRadius: theme.shape.borderRadius,
      }}
    >
      <Typography
        variant="h6"
        component="h1"
        gutterBottom
        style={{ fontSize: "1rem", color: theme.palette.text.primary }}
      >
        {t("updatePassword.title")}
      </Typography>
      <TextField
        label={t("updatePassword.oldPassword")}
        type="password"
        variant="outlined"
        margin="normal"
        onChange={(e) => setOldPassword(e.target.value)}
        fullWidth
      />
      <TextField
        label={t("updatePassword.newPassword")}
        type="password"
        variant="outlined"
        margin="normal"
        onChange={(e) => setNewPassword(e.target.value)}
        fullWidth
      />
      <TextField
        label={t("updatePassword.confirmNewPassword")}
        type="password"
        variant="outlined"
        margin="normal"
        onChange={(e) => setConfirmNewPassword(e.target.value)}
        fullWidth
      />
      <Button
        variant="contained"
        color="primary"
        size="large"
        style={{ marginTop: theme.spacing(2) }}
        onClick={handleUpdateProfile}
      >
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          t("updatePassword.updateButton")
        )}
      </Button>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </form>
  );
};

export default UpdateUserPassword;
